import { ClassValue, clsx } from 'clsx';
import { getCookies } from 'cookies-next';
import { EventEmitter } from 'fbemitter';
import posthog from 'posthog-js';
import { twMerge } from 'tailwind-merge';

import type { DataConnection } from '@/apis/data_connectors/types';
import { getMicrosoftClickId } from '@/lib/analytics/microsoft-ads';
import { getTikTokClickId, hasTikTokMarketingConsent } from '@/lib/analytics/tiktok';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isTouchDevice() {
  return typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
}

export const eventEmitter = new EventEmitter();

let GA_CLIENT_ID: string | undefined = undefined;

export function replacePipeCharacter(input) {
  if (!input) {
    return null;
  }
  return input?.replace(/\|/g, '_');
}

export function removeDevPrefix(userId) {
  if (userId?.startsWith('aaa_') || userId?.startsWith('stg_')) {
    return userId.substring(4);
  } else if (userId?.startsWith('cy_')) {
    return userId.substring(12);
  }
  return userId;
}

export const convertBlobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsDataURL(blob);
  });

//Donwload url into blob, save blob for a user
export const downloadUrlAsFile = async (url: string, filename: string): Promise<boolean> => {
  let href = url;
  const isRemote = !url.startsWith('data:') && !url.startsWith('blob:');
  if (isRemote) {
    try {
      const response = await fetch(url);
      if (!response.ok) return false;
      href = URL.createObjectURL(await response.blob());
    } catch {
      return false;
    }
  }
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (isRemote) setTimeout(() => URL.revokeObjectURL(href), 1000);
  return true;
};

async function getAnalyticsClientId(timeoutMs: number): Promise<undefined> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      resolve(undefined);
    }, timeoutMs);

    window.gtag('get', 'G-LJ8EBP0YTG', 'client_id', (client_id) => {
      clearTimeout(timeoutId);
      GA_CLIENT_ID = client_id as string;
      resolve(undefined);
    });
  });
}

export const customFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const defaultHeaders = {
    'Gcs': 'true',
    'interactive-charts': 'true',
    'use-dict': 'true'
  };

  try {
    /**
     * is-demo: A header used to identify if the user is a temp user.
     *
     * Basically we MUST only set is-demo if the user is not logged in. If we set it for logged in users,
     * the api seems to take is-demo as higher priority than the Authorization header.
     *
     * Here, we're parsing out the token in the Authorization header and checking if its there.
     *  If not, we set is-demo.
     */
    if (typeof window !== 'undefined') {
      let tempUserId = localStorage.getItem('tempUserId');

      if (tempUserId) {
        let token = null;

        if (options.headers && typeof options.headers === 'object') {
          if ('Authorization' in options.headers) {
            const bearer = options.headers['Authorization'];
            if (typeof bearer === 'string') {
              token = bearer?.split(' ')[1];
            }
          }
        }

        if (token) {
          // if there is a token, let's just delete the is-demo header
          // since they must be logged in
          localStorage.removeItem('tempUserId');
        } else {
          // There is no token, so it must be a temp user
          if (tempUserId) defaultHeaders['is-demo'] = tempUserId;
        }
      }
    }
  } catch (e) {
    console.error('Error getting token', e);
  }

  try {
    if (!GA_CLIENT_ID && typeof window !== 'undefined' && typeof window.gtag === 'function') {
      void getAnalyticsClientId(500);
    }
  } catch (_error) {
    // Ignore error, GA_CLIENT_ID will be undefined
    // adblocked or gtag hasn't loaded yet
  }

  const mergedOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options?.headers || {})
    }
  };

  if (GA_CLIENT_ID) mergedOptions.headers['ga-client-id'] = GA_CLIENT_ID;
  try {
    const sessionId = posthog.get_session_id();
    if (sessionId) mergedOptions.headers['session-id'] = sessionId;
  } catch (_error) {
    console.warn('Error getting session id', _error);
  }

  // If Cookiebot marketing consent is granted, forward Facebook cookies if present
  try {
    if (typeof window !== 'undefined' && (window as any).Cookiebot?.consent?.marketing) {
      const cookies = await getCookies();
      const fbp = cookies?._fbp;
      const fbc = cookies?._fbc;
      if (fbp) mergedOptions.headers['fbp'] = fbp;
      if (fbc) mergedOptions.headers['fbc'] = fbc;
      const msclkid = getMicrosoftClickId();
      if (msclkid) mergedOptions.headers['Msclkid'] = msclkid;
    }
  } catch (_error) {
    console.warn('Error getting marketing attribution', _error);
  }

  // If Cookiebot marketing consent is granted, forward LinkedIn cookie if present
  try {
    if (typeof window !== 'undefined' && (window as any).Cookiebot?.consent?.marketing) {
      const cookies = await getCookies();
      const liFatId = cookies?.li_fat_id;
      if (liFatId) mergedOptions.headers['li-fat-id'] = liFatId;
    }
  } catch (_error) {
    console.warn('Error getting LinkedIn cookies', _error);
  }

  // Forward TikTok attribution only when Cookiebot marketing consent is granted.
  try {
    if (hasTikTokMarketingConsent()) {
      const ttclid = getTikTokClickId();
      const cookies = await getCookies();
      const ttp = cookies?._ttp;
      if (ttclid) mergedOptions.headers['Ttclid'] = ttclid;
      if (ttp) mergedOptions.headers['Ttp'] = ttp;
      mergedOptions.headers['TikTok-Marketing-Consent'] = 'granted';
    }
  } catch (_error) {
    console.warn('Error getting TikTok attribution', _error);
  }

  const response = await fetch(url, mergedOptions);

  // Look for JWT invalidated error from backend
  // If JWT invalidated error: send to logout page
  // Else throw/return result
  try {
    if (response.status === 498) {
      const data = await response.json();
      if (data.code === 'session_expired') {
        // JWT invalidated error
        // Navigate to /logout
        window.location.href = '/logout';
        return;
      }
    }
  } catch (error) {
    console.error("Error checking for 'Invalid token':", error);
  }

  return response;
};

export const normalizeStripeUsername = (username) => {
  return replacePipeCharacter(username).replace(/\./g, '-dot-');
};

export const formatAuth0ErrorTitle = (error) => {
  return error
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const checkMobile = () => {
  //this gets called all the time; don't do logging in here
  if (typeof window === 'undefined') return false;
  try {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileDevices = [
      'android',
      'webos',
      'iphone',
      'ipad',
      'ipod',
      'blackberry',
      'iemobile',
      'opera mini'
    ];
    return mobileDevices.some((device) => userAgent.includes(device));
  } catch (error) {
    console.error('Error checking mobile device:', error);
    return false;
  }
};

export const windowHasNative = () => {
  return typeof window !== 'undefined' && window.ReactNativeWebView;
};

export const sendToNative = (payload) => {
  if (window && window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify(payload));
  }
};

const SQL_DB_TYPE_KEYWORDS = [
  'sql',
  'postgres',
  'postgre',
  'snowflake',
  'bigquery',
  'mysql',
  'supabase',
  'oracle'
];

const CODE_LANGUAGE_OVERRIDES: Array<{ keyword: string; language: string }> = [
  { keyword: 'google ads', language: 'gaql' },
  { keyword: 'gaql', language: 'gaql' },
  { keyword: 'meta ads', language: 'meta-ads' },
  { keyword: 'mcp', language: 'mcp' },
  { keyword: 'python', language: 'python' }
];

export const getCodeLanguageForConnection = (connection?: DataConnection | null): string => {
  const identifier =
    connection?.db_type?.toLowerCase() ||
    connection?.type?.toLowerCase() ||
    connection?.name?.toLowerCase();

  if (!identifier) return 'sql';

  for (const override of CODE_LANGUAGE_OVERRIDES) {
    if (identifier.includes(override.keyword)) {
      return override.language;
    }
  }

  if (SQL_DB_TYPE_KEYWORDS.some((keyword) => identifier.includes(keyword))) {
    return 'sql';
  }

  return 'sql';
};
